"use client";

import Image from "next/image";
import { useCallback, useMemo, useState } from "react";

type PressItem = {
    title: string;
    link: string;
    image: string; // public path
    pressName: string;
};

const mockPress: PressItem[] = [
    {
        title: "Unraveling long COVID: Here’s what scientists who study the illness want to find out",
        link: "https://www.npr.org/sections/health-shots/2023/09/09/1198342040/long-covid-causes-treatment-research",
        image: "/images/marketing/press/npr.png",
        pressName: "NPR",
    },
    {
        title: "What patients find at long COVID clinics: rejection, outdated therapies, and unanswered questions",
        link: "https://www.popsci.com/health/long-covid-care-chronic-illness/",
        image: "/images/marketing/press/popular-science.jpg",
        pressName: "Popular Science",
    },
    {
        title: "In Theranos’s backyard, a health tech leader takes another crack at better blood tests",
        link: "https://www.statnews.com/2023/03/07/blood-test-home-stanford-theranos-snyder/",
        image: "/images/marketing/press/stat.png",
        pressName: "Stat News",
    },
    {
        title: "Biocom California Names the Eighth Annual Life Science Catalyst Award Winners",
        link: "https://news.biocom.org/news-releases/press-release-details/2023/Biocom-California-Names-the-Eighth-Annual-Life-Science-Catalyst-Award-Winners/default.aspx",
        image: "/images/marketing/press/biocom-california.png",
        pressName: "Biocom California",
    },
    {
        title: "Our favorite startups from YC’s Winter 2022 Demo Day, part 1",
        link: "https://techcrunch.com/2022/03/29/our-favorite-startups-from-ycs-winter-2022-demo-day-part-1/",
        image: "/images/marketing/press/tech-crunch.png",
        pressName: "TechCrunch",
    },
    {
        title: "Long COVID Has Forced a Reckoning for One of Medicine’s Most Neglected Diseases",
        link: "https://www.theatlantic.com/health/archive/2022/09/mecfs-chronic-fatigue-syndrome-doctors-long-covid/671518/",
        image: "/images/marketing/press/the-atlantic.jpg",
        pressName: "The Atlantic",
    },
    {
        title: "Meet the first batch of Bay Area startups pitching at YC Demo Day",
        link: "https://www.bizjournals.com/sanfrancisco/inno/stories/news/2022/03/29/ycombinator-demo-day-2022-part1.html",
        image: "/images/marketing/press/bayarea-inno.jpg",
        pressName: "The Business Journals",
    },
    {
        title: "Stella and Rthm Partner to Treat Long COVID",
        link: "https://www.prnewswire.com/news-releases/stella-and-rthm-partner-to-treat-long-covid-301561432.html",
        image: "/images/marketing/press/cision-pr-newswire.png",
        pressName: "Cision",
    },
    {
        title: "Todos Medical Announces First Long COVID Clinic Laboratory Services Agreement for Long COVID Panel Biomarker Partnership with Amerimmune Diagnostics",
        link: "https://www.globenewswire.com/news-release/2022/08/05/2493390/0/en/Todos-Medical-Announces-First-Long-COVID-Clinic-Laboratory-Services-Agreement-for-Long-COVID-Panel-Biomarker-Partnership-with-Amerimmune-Diagnostics.html",
        image: "/images/marketing/press/todos-medical.jpg",
        pressName: "Globe Newswire",
    },
    {
        title: "A geneticist studied one drop of his blood – and saw things he couldn’t from a vial of blood",
        link: "https://www.usatoday.com/story/news/health/2023/01/19/stanford-michael-snyder-microsample-blood-health-insights/11076042002/",
        image: "/images/marketing/press/usa-today.png",
        pressName: "USA Today",
    },
];

export default function Press({ items = mockPress }: { items?: PressItem[] }) {
    const [index, setIndex] = useState(0);

    const count = items.length;

    const prev = useCallback(() => {
        setIndex((i) => (i - 1 + count) % count);
    }, [count]);

    const next = useCallback(() => {
        setIndex((i) => (i + 1) % count);
    }, [count]);

    const current = useMemo(() => items[index], [items, index]);

    if (!count) return null;

    return (
        <div className="w-full flex flex-col items-center gap-4">
            <div className="relative w-full max-w-4xl overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm">
                <a
                    href={current.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-0 md:gap-6 items-stretch"
                >
                    <div className="relative h-56 md:h-full w-full">
                        <Image
                            src={current.image}
                            alt={current.title}
                            fill
                            className="object-cover"
                            sizes="(min-width: 768px) 40vw, 100vw"
                            priority={false}
                        />
                    </div>
                    <div className="flex flex-col justify-center gap-2 p-4 md:p-6">
                        <div className="text-sm uppercase tracking-wide text-[#1a5b77] dark:text-blue-300 font-medium">
                            {current.pressName}
                        </div>
                        <h3 className="text-xl md:text-2xl font-semibold text-[#333333] dark:text-white">
                            {current.title}
                        </h3>
                        <span className="text-[#1a5b77] dark:text-blue-300 text-sm underline underline-offset-4 group-hover:no-underline">
                            Read article →
                        </span>
                    </div>
                </a>

                <button
                    aria-label="Previous press item"
                    onClick={prev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 p-2 hover:bg-white dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[#1a5b77]"
                >
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M15 18l-6-6 6-6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
                <button
                    aria-label="Next press item"
                    onClick={next}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 p-2 hover:bg-white dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[#1a5b77]"
                >
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M9 6l6 6-6 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </div>

            <div className="flex items-center gap-2">
                {items.map((_, i) => (
                    <button
                        key={i}
                        aria-label={`Go to press item ${i + 1}`}
                        onClick={() => setIndex(i)}
                        className={`h-2 w-2 rounded-full transition-colors ${
                            i === index
                                ? "bg-[#1a5b77] dark:bg-blue-300"
                                : "bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500"
                        }`}
                    />
                ))}
            </div>
        </div>
    );
}
